<template>
<BContainer class="container-pages" v-if="dataV3.data.newspaper">
    <BRow>
      <BCol xl="8">
          <BRow>
              <BCol>
                  <div class="shadow rounded p-element-page bg-white">
                     <h1 class="h1-title">{{ dataV3.data.newspaper.title }}</h1>
                     <NuxtLink :to="routePath({name: 'vesti'})">
                        <h2 class="h2-subtitle text-uppercase text-info">Vesti</h2>
                    </NuxtLink>                
                      <hr>
                  </div>
                  <div class="a-container mb-3" v-if="$screen.showMobileAds">
                    <OtherMobile></OtherMobile>
                   </div>
                   <div class="shadow rounded p-element-page bg-white mb-2 mt-2">
                    <div class="ql-editor p-0" v-html="dataV3.data.newspaper.content"></div>
                  </div> 
                  <div class="a-container mb-3" v-if="$screen.isProduction">
                    <OtherBottom></OtherBottom>
                  </div>
              </BCol>
          </BRow>
      </BCol>
      <BCol xl="4">
        <div class="a-container mb-3" v-if="$screen.showDesktopAds">
                <OtherTopRight></OtherTopRight>
            </div>
        <WidgetsAdvertisementTopAdsPages  
                      v-if="dataV3?.data?.topAds"
                         :items="dataV3?.data?.topAds?.data"  
                         :category_name="''"
                         :display_img="true"></WidgetsAdvertisementTopAdsPages>
     </BCol>
    </BRow>
  </BContainer>
 
</template>

<script setup>

import { BCol, BContainer, BFormSelect, BRow } from 'bootstrap-vue-next';
  
import { CmsNewspaperDisplay } from '#components'
import { useShareMetaStore } from "~/stores/shareMeta";
const shareMeta = useShareMetaStore();
import OtherMobile from '~/components/GoogleAdvertisements/OtherMobile.vue';
import OtherBottom from '~/components/GoogleAdvertisements/OtherBottom.vue';
import OtherTopRight from '~/components/GoogleAdvertisements/OtherTopRight.vue';
import { newspaperStore } from "~/stores/newspaper";

const useStore = newspaperStore();
const config = useRuntimeConfig()

const isProdMode = ref(config.public.isProdMode)

const device = useDevice()

const route = useRoute();

const name = computed(() => 'Vesti') // ili iz rute/filtera

const routePath = useRoutePath()

const titleParam = computed(() => String(route.params.title.split('-').join(' '))) // ili iz rute/filtera

const { data: dataV3, pending, error, refresh } = await useAsyncData(
  () => `newspapers-details-${name.value}-d${titleParam.value}`,
  // slug = deo linka kakav jeste (nove vesti); title = stari nacin za vesti bez sluga
  () => useStore.getNewspaperListingDetails({ name: name.value, title: titleParam.value, slug: String(route.params.title) }),
  {
    watch: [() => name.value, () => titleParam.value],
  }
)


// ===== SEO / AI meta =====
// vest sa slug-om: kanonski link je uvek po slug-u (i kad je otvorena starim linkom iz naslova)
const canonicalUrl = computed(() => {
  const slug = dataV3.value?.data?.newspaper?.slug
  return `https://pijace.com${slug ? route.path.replace(/[^/]+\/?$/, slug) : route.fullPath}`
})

const n = computed(() => dataV3.value?.data?.newspaper ?? null)

const headTitle = computed(() => {
  const item = n.value
  return item?.title ? item.title : 'Vesti | Pijace.com'
})

const headDescription = computed(() => {
  const item = n.value
  return (
    item?.description_meta ??
    'Poljoprivredne vesti i novosti iz Srbije, informacije sa pijaca i tržišta.'
  )
})

const ogImage = computed(() => {
  const item = n.value
  // preporuka: apsolutni URL ako možeš
  return item?.cover_img ?? item?.img ?? null
})

// (opciono) keywords/tagovi – prilagodi poljima koja imaš
const keywords = computed(() => {
  const base = [
    'poljoprivreda',
    'poljo vesti',
    'agrar',
    'pijace',
    'cene',
    'tržište hrane',
    'Srbija',
  ]

  // ako imaš tagove u bazi, npr item.tags = ['kukuruz','mleko']
  const item = n.value
  const tags = Array.isArray(item?.tags) ? item.tags : []

  return [...new Set([...base, ...tags])].join(', ')
})

// JSON-LD: NewsArticle (glavni schema entitet)
const articleLd = computed(() => {
  const item = n.value
  if (!item) return null

  // najbolje je da datePublished/dateModified budu ISO stringovi (npr. 2026-01-02T10:00:00Z)
  const datePublished = item.date_published ?? item.created_at ?? null
  const dateModified = item.date_modified ?? item.updated_at ?? datePublished ?? null

  const image = ogImage.value ? [ogImage.value] : undefined

  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: item.title,
    description: headDescription.value,
    image,
    datePublished,
    dateModified,
    inLanguage: 'sr',
    author: {
      '@type': 'Organization',
      name: 'Pijace.com',
      url: 'https://pijace.com',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Pijace.com',
      url: 'https://pijace.com',
      logo: {
        '@type': 'ImageObject',
        url: 'https://pijace.com/images/pijace-60.png',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl.value,
    },
  }
})

// JSON-LD: BreadcrumbList — Pijace.com → Vesti → {naslov}
const breadcrumbLd = computed(() => {
  if (!n.value) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Pijace.com',
        item: 'https://pijace.com'
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Vesti',
        item: 'https://pijace.com/vesti/poljoprivredne-novosti/1'
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: n.value.title,
        item: canonicalUrl.value
      }
    ]
  }
})

watchEffect(() => {
  if (!n.value) return
  shareMeta.setMeta({
    title: headTitle.value,
    description: headDescription.value,
    url: canonicalUrl.value,
  });
});

usePijaceSeo({
  title: headTitle,
  description: headDescription,
  url: canonicalUrl,
  type: 'article',
  image: ogImage,
  jsonLd: () => [articleLd.value, breadcrumbLd.value],
})

// keywords + robots (specifično za article stranice — nije pokriveno composable-om)
useHead(() => ({
  meta: [
    { name: 'keywords', content: keywords.value },
    {
      name: 'robots',
      content: 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1',
    },
  ],
}))

</script>

<style>

</style>